import React, { useState, useRef } from "react";
import { Formik } from 'formik';
import * as Yup from 'yup';
import { registerUser } from "../../../_actions/user_actions";
import { useDispatch, connect } from "react-redux";
import { useHistory } from 'react-router-dom';
import { Form, Input, Row, Button, message, Switch } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import RegisterConsentModel from './RegisterConsentModel';
import './Register.css';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 9 },
  },
};



function RegisterPage(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef();
  const [checked, setChecked] = useState({checked: true});
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null);

  const handleSwitch = e => {
    setChecked(e)
    formRef.current?.resetForm()
  }

  const handleGoBack = () => {
    history.goBack();
  };

  const handleConsentComplete = (completedAgreements) => {
    console.log('User agreed to:', completedAgreements);
    
    // Proceed with registration after consent is given
    if (pendingRegistrationData) {
      const { dataToSubmit, setSubmitting } = pendingRegistrationData;
      
      dispatch(registerUser(dataToSubmit)).then(response => {
        setSubmitting(false);
        if (response.payload.success) {
          message.success("Successfully registered, now login");
          props.history.push("/login");
        } else {
          message.error(response.payload.message);
        }
      });
    }
    
    setShowConsentModal(false);
    setPendingRegistrationData(null);
  };

  const handleConsentCancel = () => {
    console.log('User declined consent');
    
    // Reset form submission state
    if (pendingRegistrationData) {
      pendingRegistrationData.setSubmitting(false);
    }
    
    setShowConsentModal(false);
    setPendingRegistrationData(null);
    message.warning("Registration cancelled. You must agree to the terms to proceed.");
  };

  return (

    <Formik
      innerRef={formRef}
      initialValues={{
        email: '',
        lastName: '',
        firstName: '',
        password: '',
        confirmPassword: '',
        addCode: ''
      }}
      validationSchema={checked ? Yup.object().shape({
        firstName: Yup.string()
          .required('First name is required')
          .trim('First Name cannot include leading and trailing spaces')
          .max(15)
          .min(2),
        lastName: Yup.string()
          .required('Last initial is required')
          .trim('Last initial cannot include leading and trailing spaces')
          .max(1, 'Last initial must be exactly 1 character')
          .min(1, 'Last initial must be exactly 1 character'),
        email: Yup.string()
          .min(4, "Username must be at least 4 characters")
          .matches(/^@[a-zA-Z][a-zA-Z0-9]*$/, "Username must start with @ followed by a letter and then letters/numbers")
          .required('Username is required'),
        addCode: Yup.string()
          .required('Add code is required')
          .max(11, 'Must be less than 11 characters')
          .min(9, 'Must be greater than 9 characters'),
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'Passwords must match')
          .required('Confirm Password is required')
      }) :
    
        Yup.object().shape({
        firstName: Yup.string()
          .required('First name is required')
          .trim('First Name cannot include leading and trailing spaces')
          .max(15)
          .min(2),
        lastName: Yup.string()
          .required('Last Name is required')
          .trim('Last Name cannot include leading and trailing spaces')
          .max(15)
          .min(1),
        email: Yup.string()
          .email()
          .required('Email is required'),
        addCode: Yup.string()
          .required('Add code is required')
          .max(11, 'Must be less than 11 characters')
          .min(9, 'Must be greater than 9 characters'),
        password: Yup.string()
          .min(6, 'Password must be at least 6 characters')
          .required('Password is required'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'Passwords must match')
          .required('Confirm Password is required')
      })
    }

      onSubmit={(values, { setSubmitting }) => {
        let dataToSubmit = {
          email: values.email.toLowerCase(),
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          addCode: values.addCode
        };

        // Store the registration data and show consent modal
        setPendingRegistrationData({ dataToSubmit, setSubmitting });
        setShowConsentModal(true);
      }}
    >
      {props => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit
        } = props;

        return (
          <div className="register-container fade-in">
            <div className="register-form-wrapper form-transition">
              <Button 
                type="text" 
                icon={<ArrowLeftOutlined />} 
                onClick={handleGoBack}
                className="back-button button-transition"
                style={{ 
                  position: 'absolute', 
                  top: '15px', 
                  left: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  color: '#666'
                }}
              >
                Back
              </Button>
              
              <h2 className="register-title">Sign Up</h2>
              <p className="register-subtitle">Create your SchoolBucks account</p>
              
              <div className="user-type-switch">
                <Switch 
                  onChange={handleSwitch}
                  checkedChildren="Student" 
                  unCheckedChildren="Staff" 
                  defaultChecked 
                  className="switch-transition"
                />
              </div>
              
              <Form className="register-form form-transition" {...formItemLayout} onSubmit={handleSubmit} >

              <Form.Item required label="First Name">
                <Input
                  id="firstName"
                  placeholder="Enter your first name"
                  type="text"
                  value={values.firstName}
                  onChange={e =>{
                    if (checked) {
                      const firstName = e.target.value ? e.target.value.replace(/\s/g,'') : '';
                      const lastName = values.lastName ? values.lastName[0] : '';
                      values.email = ("@" + lastName + firstName).toLowerCase();
                      e.target.value = firstName;
                    }
                    handleChange(e)
                  }}
                  onBlur={handleBlur}
                  className={
                    errors.firstName && touched.firstName ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.firstName && touched.firstName && (
                  <div className="register-input-feedback error-fade-in">{errors.firstName}</div>
                )}
              </Form.Item>

              <Form.Item required label={checked ? "Last Initial" : "Last Name"}>
                <Input
                  id="lastName"
                  placeholder={checked ? "Enter your last initial" : "Enter your last name"}
                  type="text"
                  value={values.lastName}
                  onChange={e =>{
                    if (checked) {
                      const lastInitial = e.target.value ? e.target.value[0].replace(/\s/g,'') : '';
                      const firstName = values.firstName ? values.firstName.replace(/\s/g,'') : '';
                      values.email = ("@" + lastInitial + firstName).toLowerCase();
                      e.target.value = lastInitial;
                    }
                    handleChange(e)
                  }}
                  onBlur={handleBlur}
                  className={
                    errors.lastName && touched.lastName ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.lastName && touched.lastName && (
                  <div className="register-input-feedback error-fade-in">{errors.lastName}</div>
                )}
              </Form.Item>

              <Form.Item required label={checked ? "UserName" : "Email"} hasFeedback validateStatus={errors.email && touched.email ? "error" : touched.email && !errors.email ? 'success' : ''}>
                <Input
                  id="email"
                  readOnly={checked}
                  placeholder={checked ? "User Name with @" : "Email"}
                  type={checked ? "text" : "email"}
                  value={values.email}
                  onChange={e => {
                    e.target.value = (e.target.value.replace(/\s/g,'')).toLowerCase()
                    handleChange(e)
                  }}
                  onBlur={e => {
                    e.target.value = (e.target.value.replace(/\s/g,'')).toLowerCase()
                    handleBlur(e)
                  }}
                  className={
                    errors.email && touched.email ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.email && touched.email && (
                  <div className="register-input-feedback error-fade-in">{errors.email}</div>
                )}
              </Form.Item>

              <Form.Item required label="Add Code">
                <Input
                  id="addCode"
                  placeholder="Enter Add Code"
                  type="text"
                  value={values.addCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.addCode && touched.addCode ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.addCode && touched.addCode && (
                  <div className="register-input-feedback error-fade-in">{errors.addCode}</div>
                )}
              </Form.Item>

              <Form.Item required label="Password" hasFeedback validateStatus={errors.password && touched.password ? "error" : touched.password && !errors.password ? 'success' : ''}>
                <Input
                  id="password"
                  placeholder="Enter your password"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.password && touched.password ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.password && touched.password && (
                  <div className="register-input-feedback error-fade-in">{errors.password}</div>
                )}
              </Form.Item>

              <Form.Item required label="Confirm" hasFeedback validateStatus={errors.confirmPassword && touched.confirmPassword ? "error" : touched.confirmPassword && !errors.confirmPassword ? 'success' : ''}>
                <Input
                  id="confirmPassword"
                  placeholder="Enter your confirmed Password"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.confirmPassword && touched.confirmPassword ? 'text-input error input-transition' : 'text-input input-transition'
                  }
                />
                {errors.confirmPassword && touched.confirmPassword && (
                  <div className="register-input-feedback error-fade-in">{errors.confirmPassword}</div>
                )}
              </Form.Item>
              
              <div className="register-submit-section">
                <Row type="flex" justify="center" align="middle">
                  <Form.Item>
                    <Button 
                      className="register-submit-btn button-transition"
                      onClick={handleSubmit} 
                      type="primary" 
                      disabled={isSubmitting || errors.confirmPassword || errors.firstName || errors.lastName || errors.password || errors.addCode || errors.email}
                    >
                      {isSubmitting ? 'Creating Account...' : 'Sign Up'}
                    </Button>
                  </Form.Item>
                </Row>
              </div>
            </Form>

            <RegisterConsentModel
              visible={showConsentModal}
              onComplete={handleConsentComplete}
              onCancel={handleConsentCancel}
            />
          </div>
        </div>
        );
      }}
    </Formik>
  );
};

function mapStateToProps(state) {
  return {
    data: state
  };
}

export default connect(mapStateToProps)(RegisterPage)
